import { useEffect, useState } from "react";

function UnsureToggle({ q, active, onChange }) {
  return (
    <button
      type="button" role="checkbox" aria-checked={active} onClick={() => onChange(active ? null : "unsure")}
      data-testid={`checkin-unsure-${q.code}`}
      className={`mt-4 min-h-[48px] rounded-xl border-2 px-5 text-lg font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-400 ${active ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 bg-white hover:border-zinc-500"}`}
    >
      Not sure
    </button>
  );
}

// Small ranges (e.g. 0–7 days) as big tap targets; larger ranges as a numeric field.
export function NumberInput({ q, value, onChange, labelId }) {
  const unsure = value === "unsure";
  const hi = q.type === "rating" ? 10 : q.max;
  const lo = q.type === "rating" ? 0 : q.min ?? 0;
  const [text, setText] = useState(typeof value === "number" ? String(value) : "");
  useEffect(() => { setText(typeof value === "number" ? String(value) : ""); }, [q.code]); // eslint-disable-line react-hooks/exhaustive-deps

  if (hi != null && hi - lo <= 10) {
    return (
      <div>
        <div className="grid grid-cols-4 gap-2.5 sm:grid-cols-6" role="radiogroup" aria-labelledby={labelId}>
          {Array.from({ length: hi - lo + 1 }, (_, i) => lo + i).map((n) => (
            <button
              key={n} type="button" role="radio" aria-checked={value === n} onClick={() => onChange(n)}
              data-testid={`checkin-number-${q.code}-${n}`}
              className={`min-h-[56px] rounded-xl border-2 text-xl font-semibold font-mono-num transition-colors duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-400 ${value === n ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 bg-white hover:border-zinc-500"}`}
            >
              {n}
            </button>
          ))}
        </div>
        {q.unit && <p className="mt-2 text-sm text-zinc-600">Number of {q.unit}</p>}
        {q.allow_unsure && <UnsureToggle q={q} active={unsure} onChange={onChange} />}
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-center gap-3">
        <input
          id={`${labelId}-input`} aria-labelledby={labelId} inputMode="numeric" pattern="[0-9]*"
          value={unsure ? "" : text} disabled={unsure} data-testid={`checkin-input-${q.code}`}
          onChange={(e) => {
            const t = e.target.value.replace(/[^0-9.]/g, "");
            setText(t);
            onChange(t === "" ? null : Number(t));
          }}
          className="h-14 w-40 rounded-xl border-2 border-zinc-300 bg-white px-4 text-2xl font-mono-num focus:border-zinc-900 focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-400 disabled:bg-zinc-100"
        />
        {q.unit && <span className="text-lg text-zinc-700">{q.unit}</span>}
      </div>
      {q.allow_unsure && <UnsureToggle q={q} active={unsure} onChange={onChange} />}
    </div>
  );
}

export function TextInput({ q, value, onChange, labelId }) {
  const long = q.type === "long_text";
  const Tag = long ? "textarea" : "input";
  return (
    <Tag
      aria-labelledby={labelId} value={value || ""} maxLength={long ? 2000 : 200} rows={long ? 5 : undefined}
      onChange={(e) => onChange(e.target.value)} data-testid={`checkin-text-${q.code}`}
      className="w-full rounded-xl border-2 border-zinc-300 bg-white p-4 text-lg focus:border-zinc-900 focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-400"
    />
  );
}
